import { ITSTourSteps } from 'client/src/components/tour-steps';
import { TimeoutType } from 'client/src/utils/utils';
import { reducerWithInitialState } from 'typescript-fsa-reducers/dist';
import {
  LTIVars,
  resetProgressForModule,
  setClassroomMode,
  setCurrentClassroomToken,
  setCurrentStudentClassroomToken,
  setLocation,
  setLTIEnv,
  setLTIModule,
  setOpenTour,
  setRedirectTimeout,
  setTourSteps,
  setURLClassroomToken,
  setURLModule,
  updateClassSettingsFromDb,
} from '../actions/app-actions';
import { TeacherClassroom } from '../../types/classroom';
import { AnalysisType } from '../../types/analysis';
import { setCardType } from '../actions/teacher-ui-actions';
import { updateCurrentUser } from '../actions/firebase-actions';
import { ITSUser } from '@/types/user';

export enum UILocation {
  initializing = 'initializing',
  admin = 'admin',
  home = 'home',
  signup = 'signup',
  signin = 'signin',
  newAccount = 'newAccount',
  teacherHome = 'teacherHome',
  studentHome = 'studentHome',
  forgotPassword = 'forgotPassword',
  resources = 'resources',
  noRole = 'noRole',
  noContext = 'noContext',
  none = 'none',
}

export type Tour = 'initial' | 'start-up' | 'trait-confusion';
export type Tours = Tour[];
export const addTour = (tour: Tour, tours: Tours) => {
  const ret = [...tours];
  if (ret.indexOf(tour) === -1) ret.push(tour);
  return ret;
}
export const clearTour = (tour: Tour, tours: Tours) => {
  const ret = tours.filter((v)=>(v !== tour));
  return ret;
}

/** millisecond unix timestamp */
type ClassDateTs = number | undefined;
export interface AppState {
  location: UILocation;
  redirectTimeout?: TimeoutType;
  currentClassroomToken: string;
  currentClassroomStartTs: ClassDateTs;
  currentClassroomEndTs: ClassDateTs;
  currentClassroomSendGrade: boolean;
  currentStudentClassroomToken: string;
  currentCardType: AnalysisType;  // current card type selected in teacher dashboard
  urlClassroomToken?: string;
  ltiModule: string;
  ltiEnv: boolean;
  ltiServerUrl: string;
  ltiToken: string;
  classroomMode: boolean;
  openTour: boolean;
  telemetryEnabled: boolean;
  tourSteps: ITSTourSteps;
  useFirebaseForEvents: boolean;  // should logged question events be save+loaded from firebase or loki
  moduleToVersion: { [key: string]: string};
}

const INITIAL_STATE: AppState = {
  location: UILocation.home,
  currentClassroomToken: '',
  currentClassroomStartTs: -1,
  currentClassroomEndTs: -1,
  currentClassroomSendGrade: true,
  currentStudentClassroomToken: '',
  currentCardType: 'tagsAndDistractors',
  ltiModule: '',
  ltiEnv: false,
  ltiServerUrl: '',
  ltiToken: '',
  classroomMode: true,
  openTour: false,
  telemetryEnabled: true,
  tourSteps: [],
  useFirebaseForEvents: true,
  moduleToVersion: {}
};

function setLocationReducer(state: AppState, location: UILocation): AppState {
  return {
    ...state,
    location,
  };
}

function setRedirectTimeoutReducer(
  state: AppState,
  redirectTimeout?: TimeoutType
): AppState {
  return {
    ...state,
    redirectTimeout,
  };
}

function setCardTypeReducer(
  state: AppState,
  cardType: AnalysisType
): AppState {
  return { ...state, currentCardType: cardType }
}

function setCurrentClassroomTokenReducer(
  state: AppState,
  currentClassroomToken: string
): AppState {
  return {
    ...state,
    currentClassroomToken,
    currentStudentClassroomToken: currentClassroomToken
  };
}

function setCurrentStudentClassroomTokenReducer(
  state: AppState,
  currentStudentClassroomToken: string
): AppState {
  return {
    ...state,
    currentStudentClassroomToken
  };
}

function setURLClassroomTokenReducer(
  state: AppState,
  urlClassroomToken?: string
): AppState {
  return {
    ...state,
    urlClassroomToken,
  };
}

function setLTIEnvReducer(state: AppState, { ltiToken, ltijsUrl }: LTIVars): AppState {
  return {
    ...state,
    ltiEnv: true,
    ltiServerUrl: ltijsUrl || '',
    ltiToken: ltiToken || ''
  };
}

function setOpenTourReducer(state: AppState, openTour: boolean): AppState {
  return {
    ...state,
    openTour,
  };
}

function setTourStepsReducer(state: AppState, tourSteps: ITSTourSteps): AppState {
  return {
    ...state,
    tourSteps,
    openTour: true,
  };
}

function setLTIModuleReducer(state: AppState, ltiModule: string): AppState {
  return {
    ...state,
    ltiModule
  }
}

function setClassroomModeReducer(
  state: AppState,
  classroomMode: boolean
): AppState {
  return {
    ...state,
    classroomMode,
  };
}

function updateClassSettingsFromDbReducer(
  state: AppState, 
  { endTsMs, startTsMs, sendGrades, loadFromLoki }: Partial<TeacherClassroom>
): AppState {
  return {
    ...state,
    currentClassroomStartTs: startTsMs,
    currentClassroomEndTs: endTsMs,
    currentClassroomSendGrade: sendGrades ? sendGrades : false,
    useFirebaseForEvents: loadFromLoki ? !loadFromLoki : true
  }
};

/**
 * load the telemetry setting for this user
 * 
 * @param state 
 * @param user 
 * @returns 
 */
function updateCurrentUserReducer(state: AppState, user: ITSUser | undefined): AppState {
  if (user && user.telemetry !== undefined) {
    return {
      ...state,
      telemetryEnabled: user.telemetry
    }
  }
  return state;
}

function resetProgressForModuleReducer(
  state: AppState,
  { module, version }: {module: string, version: string | undefined}
): AppState {
  if (module && version) {
    const newModuleToVersion = { ...state.moduleToVersion };
    newModuleToVersion[module] = version;
    return {
      ...state,
      moduleToVersion: newModuleToVersion,
    };
  }
  return state;
}

export const appReducers = reducerWithInitialState(INITIAL_STATE)
  .case(updateCurrentUser, updateCurrentUserReducer)
  .case(setLocation, setLocationReducer)
  .case(setRedirectTimeout, setRedirectTimeoutReducer)
  .case(setCardType, setCardTypeReducer)
  .case(setCurrentClassroomToken, setCurrentClassroomTokenReducer)
  .case(setCurrentStudentClassroomToken, setCurrentStudentClassroomTokenReducer)
  .case(setURLClassroomToken, setURLClassroomTokenReducer)
  .case(setLTIEnv, setLTIEnvReducer)
  .cases([setLTIModule, setURLModule], setLTIModuleReducer)
  .case(setClassroomMode, setClassroomModeReducer)
  .case(setOpenTour, setOpenTourReducer)
  .case(setTourSteps, setTourStepsReducer)
  .case(updateClassSettingsFromDb, updateClassSettingsFromDbReducer)
  .case(resetProgressForModule, resetProgressForModuleReducer)
  .build();
